import React, { useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  BsListCheck,
  BsCalendarCheck,
  BsCashCoin,
  BsShieldCheck,
  BsTagFill,
  BsGraphUpArrow,
  BsHeadset,
} from "react-icons/bs";

import ListerWizard from "./ListerWizard";

const HOW_IT_WORKS = [
  {
    icon: <BsListCheck />,
    title: "1. List your facility",
    text: "Tell us about your space — or let our team build the listing for you, free of charge.",
  },
  {
    icon: <BsCalendarCheck />,
    title: "2. Renters book online",
    text: "Thousands of renters search FindxStorage every month. Bookings come to you automatically.",
  },
  {
    icon: <BsCashCoin />,
    title: "3. Get paid securely",
    text: "Payments are processed through Stripe and paid out directly to your bank account.",
  },
];

const TRUST_POINTS = [
  { icon: <BsTagFill />, text: "Free to list — no upfront fees" },
  { icon: <BsGraphUpArrow />, text: "You control your prices and promotions" },
  { icon: <BsShieldCheck />, text: "Secure payments powered by Stripe" },
  { icon: <BsHeadset />, text: "Real humans to help you get set up" },
];

const ListYourStorage = () => {
  const [searchParams] = useSearchParams();
  const wizardRef = useRef(null);

  // Campaign attribution — carried into the lead payload
  const utmParams = {
    utm_source: searchParams.get("utm_source") || "",
    utm_medium: searchParams.get("utm_medium") || "",
    utm_campaign: searchParams.get("utm_campaign") || "",
  };

  const scrollToWizard = () => {
    wizardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div>
      {/* HERO */}
      <section style={{ backgroundColor: "#000034" }} className="text-white py-5">
        <div className="container py-4">
          <div className="row align-items-center">
            <div className="col-lg-7 text-center text-lg-start">
              <span className="badge bg-orange text-dark mb-3">For storage owners</span>
              <h1 className="fw-bold display-5">
                Turn your empty storage space into <span className="mineTextOrange">income</span>
              </h1>
              <p className="lead mt-3" style={{ maxWidth: "540px" }}>
                Join FindxStorage and reach thousands of renters looking for storage near you.
                Listing is free — and if you're busy, we'll even build your listing for you.
              </p>
              <div className="d-flex gap-3 justify-content-center justify-content-lg-start mt-4">
                <button className="btn bg-orange text-dark fw-semibold px-4 py-2" onClick={scrollToWizard}>
                  Get started — it's free
                </button>
                <a href="#how-it-works" className="btn btn-outline-light px-4 py-2">
                  How it works
                </a>
              </div>
            </div>
            <div className="col-lg-5 d-none d-lg-block">
              <img
                src="/images/FindxStorage3.png"
                alt="FindxStorage"
                className="img-fluid p-4"
                style={{ maxHeight: "220px" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-5 bg-light">
        <div className="container">
          <h2 className="text-center blueTextColr fw-bold mb-5">How it works</h2>
          <div className="row g-4">
            {HOW_IT_WORKS.map((item) => (
              <div className="col-md-4" key={item.title}>
                <div className="card h-100 border-0 shadow-sm text-center p-4" style={{ borderRadius: "20px" }}>
                  <span className="mineTextOrange mx-auto mb-3" style={{ fontSize: "2.5rem" }}>
                    {item.icon}
                  </span>
                  <h5 className="fw-bold blueTextColr">{item.title}</h5>
                  <p className="text-muted mb-0">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <h2 className="blueTextColr fw-bold mb-4 text-center">Why list on FindxStorage?</h2>
              <div className="row g-3">
                {TRUST_POINTS.map((point) => (
                  <div key={point.text} className="col-md-6">
                    <div className="d-flex align-items-center">
                      <span
                        className="bg-orange text-dark d-inline-flex align-items-center justify-content-center me-3"
                        style={{ width: "42px", height: "42px", borderRadius: "12px", fontSize: "1.2rem", flexShrink: 0 }}
                      >
                        {point.icon}
                      </span>
                      <span className="fs-5">{point.text}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-center mt-4">
                <button className="btn bg-orange text-dark fw-semibold px-4 py-2" onClick={scrollToWizard}>
                  Get started — it's free
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WIZARD */}
      <section ref={wizardRef} className="py-5" style={{ backgroundColor: "#000034", scrollMarginTop: "6rem" }}>
        <div className="container">
          <h2 className="text-center text-white fw-bold mb-2">
            Let's get your space <span className="mineTextOrange">listed</span>
          </h2>
          <p className="text-center text-white-50 mb-5">
            Answer a few quick questions — do it yourself, or let us handle everything.
          </p>
          <ListerWizard utmParams={utmParams} />
        </div>
      </section>
    </div>
  );
};

export default ListYourStorage;
